
import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import { useAppContext } from '../contexts/AppContext';
import { RefreshCw, Grid3x3, MousePointerClick, Move } from 'lucide-react';
import { DfrData, FmeaItem } from '../types';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import ActiveProjectBadge from '../components/ActiveProjectBadge';

// --- TYPE DEFINITIONS ---
type GraphNodeKind = 'p' | 'f' | 'c-prev' | 'c-det' | 'd';

interface D3Node extends d3.SimulationNodeDatum {
    id: string;
    kind: GraphNodeKind;
    text: string;
    function: string;
    w: number;
    h: number;
    x?: number;
    y?: number;
    vx?: number;
    vy?: number;
    fx?: number | null;
    fy?: number | null;
    _gridX?: number;
    _gridY?: number;
}

interface D3Link extends d3.SimulationLinkDatum<D3Node> {
    source: string | D3Node;
    target: string | D3Node;
    color: string;
    dash?: string;
}

const NODE_TYPE_MAP: Record<GraphNodeKind, string> = {
    p: 'Function',
    f: 'FMEA Risk',
    'c-prev': 'Prevention Control',
    'c-det': 'Detection Control',
    d: 'DVP&R Test',
};

// --- HELPER FUNCTION FOR HIGHLIGHTING ---
const getRelatedNodeIds = (
    centerId: string,
    kind: GraphNodeKind,
    allData: DfrData
): Set<string> => {
    const relatedIds = new Set<string>([centerId]);
    const fmeaById = new Map((allData?.fmeaItems || []).map(f => [f.id, f]));

    // Create function name to ID mapping
    const functionNameToId = new Map<string, string>();
    const functionIdToName = new Map<string, string>();
    (allData?.pDiagramList || []).forEach(p => {
        functionNameToId.set(p.systemName, p.consolidatedFunctionId);
        functionIdToName.set(p.consolidatedFunctionId, p.systemName);
    });

    const fmeaByControl = new Map<string, Set<string>>();
    (allData?.fmeaItems || []).forEach(f => {
        [...(f.preventionControls || []), ...(f.detectionControls || [])].forEach(cId => {
            if (!fmeaByControl.has(cId)) fmeaByControl.set(cId, new Set());
            fmeaByControl.get(cId)!.add(f.id);
        });
    });
    const fmeaByTest = new Map<string, Set<string>>();
    (allData?.dvprTests || []).forEach(t => {
        (t.traceableRiskId || []).forEach(fId => {
            if (!fmeaByTest.has(t.testId)) fmeaByTest.set(t.testId, new Set());
            fmeaByTest.get(t.testId)!.add(fId);
        });
    });

    const addFmeaRelations = (fmea: FmeaItem) => {
        // Add the function ID (not name!)
        const functionId = functionNameToId.get(fmea.function);
        if (functionId) {
            relatedIds.add(functionId);
        }
        relatedIds.add(fmea.id); // Also add the FMEA itself
        (fmea.preventionControls || []).forEach(c => relatedIds.add(c));
        (fmea.detectionControls || []).forEach(c => relatedIds.add(c));
        (allData?.dvprTests || []).forEach(t => {
            if ((t.traceableRiskId || []).includes(fmea.id)) relatedIds.add(t.testId);
        });
    };

    switch (kind) {
        case 'p': {
            // centerId is consolidatedFunctionId, need to find FMEAs by function name
            const functionName = functionIdToName.get(centerId);
            if (functionName) {
                (allData?.fmeaItems || []).forEach(f => {
                    if (f.function === functionName) {
                        addFmeaRelations(f);
                    }
                });
            }
            break;
        }
        case 'f': {
            const fmea = fmeaById.get(centerId);
            if (fmea) addFmeaRelations(fmea);
            break;
        }
        case 'c-prev':
        case 'c-det':
            fmeaByControl.get(centerId)?.forEach(fId => {
                const fmea = fmeaById.get(fId);
                if (fmea) addFmeaRelations(fmea);
            });
            break;
        case 'd':
            fmeaByTest.get(centerId)?.forEach(fId => {
                const fmea = fmeaById.get(fId);
                if (fmea) addFmeaRelations(fmea);
            });
            break;
    }
    return relatedIds;
};


const GraphViewPage: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const { allData, navigate, activeProject } = useAppContext();

    const [layoutMode, setLayoutMode] = useState<'grid' | 'force'>('grid');
    const [focusedId, setFocusedId] = useState<string | null>(null);
    const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set());
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    // Project filtering helper
    const isItemInProject = useCallback((projName?: string) => {
        if (!activeProject) return true; // Show all if no filter
        if (projName && projName === activeProject) return true;

        // Match project names from epoch titles if possible
        if (projName && (activeProject.includes(projName) || projName.includes(activeProject))) return true;

        const projectName = activeProject.split(' - ')[0];
        if (projName === projectName) return true;

        return false;
    }, [activeProject]);

    // Resize Observer
    useEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                setDimensions({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height
                });
            }
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    // Data Preparation
    const { nodes, links } = useMemo(() => {
        const tempNodes: D3Node[] = [];
        const validNodeIds = new Set<string>();
        const measure = (text: string) => Math.min(300, Math.max(140, text.length * 7 + 30));

        const filteredPDiagrams = (allData?.pDiagramList || []).filter(p => {
            if (!isItemInProject(p.projectName)) return false;
            // Only show active P-Diagrams (default to 'active' for legacy data without status)
            const status = p.status || 'active';
            return status === 'active';
        });
        const filteredFmeaItems = (allData?.fmeaItems || []).filter(f => isItemInProject(f.projectName));
        const filteredControls = (allData?.controls || []).filter(c => isItemInProject(c.projectName));
        const filteredDvprTests = (allData?.dvprTests || []).filter(d => isItemInProject(d.projectName));

        filteredPDiagrams.forEach(p => {
            tempNodes.push({ id: p.consolidatedFunctionId, kind: 'p', text: p.systemName, function: p.consolidatedFunctionId, w: measure(p.systemName), h: 40 });
            validNodeIds.add(p.consolidatedFunctionId);
        });
        filteredFmeaItems.forEach(f => {
            tempNodes.push({ id: f.id, kind: 'f', text: `${f.id}: ${f.failureMode}`, function: f.function, w: measure(f.failureMode) + 30, h: 40 });
            validNodeIds.add(f.id);
        });
        filteredControls.forEach(c => {
            tempNodes.push({ id: c.id, kind: c.type === 'prevention' ? 'c-prev' : 'c-det', text: `${c.id}: ${c.name}`, function: '', w: measure(c.name) + 30, h: 40 });
            validNodeIds.add(c.id);
        });
        filteredDvprTests.forEach(d => {
            tempNodes.push({ id: d.testId, kind: 'd', text: `${d.testId}: ${d.testName}`, function: d.functionName, w: measure(d.testName) + 30, h: 40 });
            validNodeIds.add(d.testId);
        });

        const tempLinks: D3Link[] = [];
        const addLink = (source: string, target: string, color: string, dash?: string) => {
            if (validNodeIds.has(source) && validNodeIds.has(target)) {
                tempLinks.push({ source, target, color, dash });
            }
        };

        // Create a map from function name to consolidatedFunctionId
        const functionNameToId = new Map<string, string>();
        filteredPDiagrams.forEach(p => {
            functionNameToId.set(p.systemName, p.consolidatedFunctionId);
        });

        filteredFmeaItems.forEach(f => {
            // Use consolidatedFunctionId instead of function name
            const functionId = functionNameToId.get(f.function);
            if (functionId) {
                addLink(functionId, f.id, '#90a4ae');
            }
            (f.preventionControls || []).forEach(c => addLink(f.id, c, '#fb8c00')); // Orange for prevention
            (f.detectionControls || []).forEach(c => addLink(f.id, c, '#1e88e5', '4,4')); // Blue dashed for detection
        });
        filteredDvprTests.forEach(d => {
            (d.traceableRiskId || []).forEach(fId => addLink(fId, d.testId, '#7cb342')); // Green for tests
        });

        return { nodes: tempNodes, links: tempLinks };
    }, [allData, isItemInProject]);

    // Calculate highlighted IDs
    useEffect(() => {
        if (!focusedId) {
            setHighlightedIds(new Set());
            return;
        }
        const node = nodes.find(n => n.id === focusedId);
        if (node) {
            const relatedIds = getRelatedNodeIds(focusedId, node.kind, allData);
            console.log(`Node clicked: ${focusedId} (${node.kind})`);
            console.log('HighlightedIds:', Array.from(relatedIds));
            setHighlightedIds(relatedIds);
        }
    }, [focusedId, nodes, allData]);

    // Main rendering effect
    useEffect(() => {
        if (nodes.length === 0 || dimensions.width === 0 || dimensions.height === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove(); // Clear everything

        const { width, height } = dimensions;
        const cx = width / 2;
        const cy = height / 2;

        // Create main group
        const g = svg.append('g');

        // Add zoom behavior
        const zoom = d3.zoom<SVGSVGElement, unknown>()
            .scaleExtent([0.1, 4])
            .on('zoom', (event) => g.attr('transform', event.transform));

        svg.call(zoom).on("dblclick.zoom", null);

        // Add arrow markers (smaller arrows)
        const defs = svg.append('defs');
        [
            { id: 'arrow-gray', color: '#90a4ae' },
            { id: 'arrow-orange', color: '#fb8c00' },
            { id: 'arrow-blue', color: '#1e88e5' },
            { id: 'arrow-green', color: '#7cb342' }
        ].forEach(({ id, color }) => {
            defs.append("marker")
                .attr("id", id)
                .attr("viewBox", "0 -5 10 10")
                .attr("refX", 10) // Adjusted for visibility
                .attr("refY", 0)
                .attr("markerWidth", 5) // Slightly larger
                .attr("markerHeight", 5) // Slightly larger
                .attr("orient", "auto")
                .append("path")
                .attr("d", "M0,-5L10,0L0,5")
                .attr("fill", color);
        });

        // Create link and node groups
        const linkGroup = g.append('g').attr('class', 'links');
        const nodeGroup = g.append('g').attr('class', 'nodes');

        // Grid layout
        if (layoutMode === 'grid') {
            // Reset zoom
            svg.transition().duration(750).call(zoom.transform, d3.zoomIdentity);

            const colWidth = 300, rowHeight = 60;
            const columnsOrder: GraphNodeKind[] = ['p', 'f', 'c-prev', 'c-det', 'd'];
            const columns = new Map(columnsOrder.map(k => [k, [] as D3Node[]]));
            nodes.forEach(n => columns.get(n.kind)?.push(n));

            const totalGridWidth = columnsOrder.length * colWidth;
            const startX = cx - (totalGridWidth / 2) + (colWidth / 2);

            columnsOrder.forEach((kind, colIndex) => {
                const list = columns.get(kind)!;
                const xPos = startX + (colIndex * colWidth);
                const totalColHeight = list.length * rowHeight;
                const startY = cy - (totalColHeight / 2);

                list.forEach((n, i) => {
                    n.x = xPos;
                    n.y = startY + (i * rowHeight);
                });
            });
        }

        // Draw links FIRST (so they appear behind nodes)
        linkGroup.selectAll('line')
            .data(links)
            .join('line')
            .attr('x1', d => (nodes.find(n => n.id === d.source)?.x || 0))
            .attr('y1', d => (nodes.find(n => n.id === d.source)?.y || 0))
            .attr('x2', d => (nodes.find(n => n.id === d.target)?.x || 0))
            .attr('y2', d => (nodes.find(n => n.id === d.target)?.y || 0))
            .attr('stroke', d => d.color)
            .attr('stroke-width', d => {
                if (!focusedId) return 2; // Reduced from 3
                const sourceId = d.source as string;
                const targetId = d.target as string;
                const isHighlighted = highlightedIds.has(sourceId) && highlightedIds.has(targetId);
                console.log(`Link ${sourceId} -> ${targetId}: highlighted=${isHighlighted}, highlightedIds size=${highlightedIds.size}`);
                return isHighlighted ? 3 : 0.3; // Reduced from 6:0.5
            })
            .attr('stroke-dasharray', d => d.dash || '')
            .attr('opacity', d => {
                if (!focusedId) return 0.9; // Increased default
                const sourceId = d.source as string;
                const targetId = d.target as string;
                const isHighlighted = highlightedIds.has(sourceId) && highlightedIds.has(targetId);
                return isHighlighted ? 1 : 0.08; // Much more dimmed: from 0.5 to 0.08
            })
            .attr('marker-end', d => {
                const colorToMarker: Record<string, string> = {
                    '#90a4ae': 'url(#arrow-gray)',
                    '#fb8c00': 'url(#arrow-orange)',
                    '#1e88e5': 'url(#arrow-blue)',
                    '#7cb342': 'url(#arrow-green)'
                };
                return colorToMarker[d.color] || 'url(#arrow-gray)';
            });

        // Draw nodes
        const styleMap: Record<GraphNodeKind, { fill: string, stroke: string, text: string }> = {
            'p': { fill: '#e8eaf6', stroke: '#3949ab', text: '#1a237e' },
            'f': { fill: '#ffebee', stroke: '#d32f2f', text: '#b71c1c' },
            'd': { fill: '#e8f5e9', stroke: '#388e3c', text: '#1b5e20' },
            'c-prev': { fill: '#fff7e6', stroke: '#fb8c00', text: '#e65100' },
            'c-det': { fill: '#e7f0ff', stroke: '#1e88e5', text: '#0d47a1' },
        };

        const nodeElements = nodeGroup.selectAll('g')
            .data(nodes, (d: any) => d.id)
            .join('g')
            .attr('transform', d => `translate(${d.x},${d.y})`)
            .style('cursor', 'pointer')
            .on('click', (event, d) => {
                if (event.ctrlKey || event.metaKey) {
                    const pathMap: Record<GraphNodeKind, string> = {
                        'p': '/p-diagram',
                        'f': '/fmeas',
                        'c-prev': '/bom-functions',
                        'c-det': '/bom-functions',
                        'd': '/dvpr'
                    };
                    navigate(pathMap[d.kind]);
                } else {
                    event.stopPropagation();
                    setFocusedId(curr => curr === d.id ? null : d.id);
                }
            });

        nodeElements.each(function (d) {
            const el = d3.select(this);
            const style = styleMap[d.kind];

            el.append('rect')
                .attr('rx', 12)
                .attr('ry', 12)
                .attr('width', d.w)
                .attr('height', d.h)
                .attr('x', -d.w / 2)
                .attr('y', -d.h / 2)
                .attr('fill', style.fill)
                .attr('stroke', style.stroke)
                .attr('stroke-width', 2);

            el.append('text')
                .text(d.text.length > 35 ? d.text.substring(0, 32) + '...' : d.text)
                .attr('dy', 5)
                .attr('text-anchor', 'middle')
                .attr('font-size', '12px')
                .attr('pointer-events', 'none')
                .style('font-family', 'sans-serif')
                .style('fill', style.text);
        });

        // Apply node highlighting
        nodeElements.style('opacity', d => !focusedId || highlightedIds.has(d.id) ? 1 : 0.1);

    }, [nodes, links, dimensions, layoutMode, focusedId, highlightedIds, navigate]);

    return (
        <div className="space-y-4 h-full flex flex-col">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary">Graph View</h1>
                    <div className="flex items-center gap-2 mt-1">
                        <p className="text-text-secondary text-sm">Interactive Dependency Graph. Click a node to focus and rearrange layout.</p>
                        <ActiveProjectBadge showStandard={false} />
                    </div>
                </div>
                <div className="flex gap-2">
                    <ModuleInfoModal
                        title="Knowledge Graph"
                        description="Interactive visualization of the relationships between Functions, Risks, Tests, and Controls."
                        benefits={[
                            "Understand the complexity and connectedness of the system.",
                            "Identify 'Spider Webs' (single points of failure affecting many functions).",
                            "Visualize the 'Golden Thread' of reliability."
                        ]}
                        usageInstructions={[
                            "Click a node to focus and highlight connected items.",
                            "Ctrl+Click (or Cmd+Click) a node to open its detail page.",
                            "Toggle between Grid and Force layout."
                        ]}
                    />
                    <button onClick={() => { setFocusedId(null); setLayoutMode('grid'); }} className="p-2 bg-card border border-border rounded hover:bg-background text-text-secondary" title="Reset View">
                        <RefreshCw size={20} />
                    </button>
                    <div className="bg-card border border-border rounded flex overflow-hidden">
                        <button onClick={() => setLayoutMode('grid')} className={`px-3 py-2 flex items-center gap-2 text-sm font-medium ${layoutMode === 'grid' ? 'bg-primary text-white' : 'hover:bg-background'}`}>
                            <Grid3x3 size={18} /> Grid
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex-1 bg-card border border-border rounded-lg shadow-sm relative overflow-hidden" ref={containerRef}>
                <svg ref={svgRef} className="w-full h-full cursor-move bg-slate-50 dark:bg-slate-900/50"></svg>

                <div className="absolute top-4 left-4 pointer-events-none bg-card/80 backdrop-blur p-2 rounded border border-border text-xs text-text-secondary shadow-sm flex flex-col gap-1">
                    <div className="flex items-center gap-2"><MousePointerClick size={14} /> Click to Focus</div>
                    <div className="flex items-center gap-2"><Move size={14} /> Drag to Pan</div>
                    <div className="flex items-center gap-2"><span>⌘</span> Ctrl+Click to Visit</div>
                </div>

                <div className="absolute bottom-4 right-4 bg-card/90 backdrop-blur p-3 rounded-lg border border-border shadow-sm">
                    <h4 className="text-xs font-bold mb-2 text-text-secondary uppercase tracking-wider">Node Types</h4>
                    <div className="space-y-1.5">
                        {Object.entries(NODE_TYPE_MAP).map(([kind, text]) => {
                            const styleMap: Record<GraphNodeKind, string> = {
                                'p': 'bg-[#e8eaf6] border-[#3949ab]',
                                'f': 'bg-red-500/10 border-red-700',
                                'd': 'bg-green-500/10 border-green-700',
                                'c-prev': 'bg-orange-500/10 border-orange-700',
                                'c-det': 'bg-blue-500/10 border-blue-700'
                            };
                            return (
                                <div key={kind} className="flex items-center gap-2">
                                    <div className={`w-3 h-3 rounded border ${styleMap[kind as GraphNodeKind]}`}></div>
                                    <span className="text-xs text-text-primary">{text}</span>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GraphViewPage;
